import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  Day,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  Interval,
  isBefore,
  max,
  min,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { weekdaysNumberMap } from 'shared/constants';
import { isBetween } from 'shared/lib/date-fns';
import { RRule } from 'shared/lib/rrule';
import { DateRange } from 'shared/types/date-range';
import { FrequencyUnit, HabitSchedule } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getScheduleInterval } from 'shared/utils/get-schedule-interval';

import { getScheduleFrequency } from '../get-schedule-frequency';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { getScheduleEndDate } from './get-schedule-end-date';

const getFirstOccurrence = (schedule: HabitSchedule) => {
  if (schedule.frequency?.count) {
    return schedule.startDate;
  }

  const rrule = RRule.fromString(schedule.rrule.format);
  return new RRule({
    ...rrule.origOptions,
    dtstart: startOfDay(schedule.startDate),
  }).after(schedule.startDate, true);
};

export const getScheduleFrequencyTimeframeByDate = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange => {
  const frequency = getScheduleFrequency(schedule);
  const dividerDate = startOfDay(schedule.startDate);

  if (frequency === FrequencyUnit.Year) {
    const startDate = isBefore(date, dividerDate)
      ? startOfYear(date)
      : max([startOfYear(date), dividerDate]);
    const endDate = isBefore(date, dividerDate)
      ? min([endOfYear(date), endOfDay(addDays(dividerDate, -1))])
      : endOfYear(date);

    return { startDate, endDate };
  }
  if (frequency === FrequencyUnit.Month) {
    const startDate = isBefore(date, dividerDate)
      ? startOfMonth(date)
      : max([startOfMonth(date), dividerDate]);
    const endDate = isBefore(date, dividerDate)
      ? min([endOfMonth(date), endOfDay(addDays(dividerDate, -1))])
      : endOfMonth(date);

    return { startDate, endDate };
  }

  const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };

  const startDate = isBefore(date, dividerDate)
    ? startOfWeek(date, dateFnsOptions)
    : max([startOfWeek(date, dateFnsOptions), dividerDate]);
  const endDate = isBefore(date, dividerDate)
    ? min([endOfWeek(date, dateFnsOptions), endOfDay(addDays(dividerDate, -1))])
    : endOfWeek(date, dateFnsOptions);

  return { startDate, endDate };
};

export const getScheduleTimeframeByDate = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange => {
  const frequency = getScheduleFrequency(schedule);
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleStart = isBefore(date, schedule.startDate)
    ? getActualScheduleStartDate(schedule)
    : scheduleStartDate;
  const interval = getScheduleInterval(schedule);
  // get the first occurrence of the schedule to have the correct calculations of the timeframe
  const firstOccurrenceAfterScheduleStart = getFirstOccurrence(schedule);
  const firstOccurrence = isBefore(date, schedule.startDate)
    ? scheduleStart
    : firstOccurrenceAfterScheduleStart ?? scheduleStart;

  if (frequency === FrequencyUnit.Year) {
    const dateStart = startOfYear(date);
    // take the interval off the first occurrence to get the start of the timeframe
    const firstTimeframeStart = addYears(
      startOfYear(firstOccurrence),
      -(interval - 1),
    );
    const diffInYears = differenceInYears(dateStart, firstTimeframeStart);
    const diffInYearsOffset = Math.floor(diffInYears / interval) * interval;
    const dateIntervalStart = startOfYear(
      addYears(firstTimeframeStart, diffInYearsOffset),
    );
    const yearStart = new Date(
      Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
    );
    const timeframeEnd = endOfMonth(addMonths(yearStart, interval - 1));

    return {
      startDate: yearStart,
      endDate: new Date(
        Math.min(
          ...(schedule.endDate
            ? [getScheduleEndDate(schedule)!.getTime()]
            : []),
          ...(isBefore(timeframeEnd, scheduleStartDate)
            ? [endOfDay(addDays(scheduleStartDate, -1)).getTime()]
            : []),
          timeframeEnd.getTime(),
        ),
      ),
    };
  }

  if (frequency === FrequencyUnit.Month) {
    const dateStart = startOfMonth(date);
    // take the interval off the first occurrence to get the start of the timeframe
    const firstTimeframeStart = addMonths(
      startOfMonth(firstOccurrence),
      -(interval - 1),
    );
    const diffInMonths = differenceInMonths(dateStart, firstTimeframeStart);
    const diffInMonthsOffset = Math.floor(diffInMonths / interval) * interval;
    const dateIntervalStart = startOfMonth(
      addMonths(firstTimeframeStart, diffInMonthsOffset),
    );
    const monthStart = new Date(
      Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
    );
    const timeframeEnd = endOfMonth(addMonths(monthStart, interval - 1));

    return {
      startDate: monthStart,
      endDate: new Date(
        Math.min(
          ...(schedule.endDate
            ? [getScheduleEndDate(schedule)!.getTime()]
            : []),
          ...(isBefore(timeframeEnd, scheduleStartDate)
            ? [endOfDay(addDays(scheduleStartDate, -1)).getTime()]
            : []),
          timeframeEnd.getTime(),
        ),
      ),
    };
  }

  const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
  const dateStart = startOfWeek(date, dateFnsOptions);
  // take the interval off the first occurrence to get the start of the timeframe
  const firstTimeframeStart = addWeeks(
    startOfWeek(firstOccurrence, dateFnsOptions),
    -(interval - 1),
  );
  const diffInWeeks = differenceInWeeks(dateStart, firstTimeframeStart);
  const diffInWeeksOffset = Math.floor(diffInWeeks / interval) * interval;
  const dateIntervalStart = startOfWeek(
    addWeeks(firstTimeframeStart, diffInWeeksOffset),
    dateFnsOptions,
  );
  const weekStart = new Date(
    Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
  );
  const timeframeEnd = endOfWeek(
    addWeeks(weekStart, interval - 1),
    dateFnsOptions,
  );

  return {
    startDate: weekStart,
    endDate: new Date(
      Math.min(
        ...(schedule.endDate ? [getScheduleEndDate(schedule)!.getTime()] : []),
        ...(isBefore(timeframeEnd, scheduleStartDate)
          ? [endOfDay(addDays(scheduleStartDate, -1)).getTime()]
          : []),
        timeframeEnd.getTime(),
      ),
    ),
  };
};

const frequencyEachMap: Record<
  FrequencyUnit,
  (interval: Interval, options: { weekStartsOn: Day }) => Timestamp[]
> = {
  [FrequencyUnit.Week]: (interval, options) =>
    eachWeekOfInterval(interval, options),
  [FrequencyUnit.Month]: (interval) => eachMonthOfInterval(interval),
  [FrequencyUnit.Year]: (interval) => eachYearOfInterval(interval),
};

export const getScheduleTimeframesByDateRange = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    weekStartsOn,
  }: { startDate: Timestamp; endDate: Timestamp; weekStartsOn: WeekDays },
): DateRange[] => {
  const frequency = getScheduleFrequency(schedule);
  const eachOf = frequencyEachMap[frequency] ?? eachWeekOfInterval;
  return eachOf(
    { start: startDate, end: endDate },
    { weekStartsOn: weekdaysNumberMap[weekStartsOn] },
  ).reduce<DateRange[]>((acc, date) => {
    const alreadyInTimeframe = acc.some(({ startDate, endDate }) =>
      isBetween(date, startDate, endDate),
    );

    if (alreadyInTimeframe) {
      return acc;
    }

    const dateRange = getScheduleTimeframeByDate(schedule, date, weekStartsOn);
    const dateRangeAlreadyInResult = acc.some(
      ({ startDate, endDate }) =>
        startDate === dateRange.startDate && endDate === dateRange.endDate,
    );

    if (dateRangeAlreadyInResult) {
      return acc;
    }

    acc.push(dateRange);
    return acc;
  }, []);
};
